import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { createTranslator, isLocale, isRtl, negotiateLocale, type Locale } from '@zt/i18n';
import { setApiLocale } from './api';

interface I18nValue {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
  formatDate: (iso: string | null | undefined, withTime?: boolean) => string;
  formatRelative: (iso: string | null | undefined) => string;
  /** Ondalık sayı, dilin biçimiyle (tr: 2,3). Para için kullanılmaz (useMoney). */
  formatNumber: (n: number, fractionDigits?: number) => string;
}

const Ctx = createContext<I18nValue | null>(null);
const STORAGE_KEY = 'zt.locale';
const INTL_TAG: Record<Locale, string> = { tr: 'tr-TR', en: 'en-GB', de: 'de-DE', ar: 'ar' };

function initialLocale(): Locale {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (isLocale(saved)) return saved;
  } catch {
    /* depolama kapalı olabilir */
  }
  return negotiateLocale(navigator.language);
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(initialLocale);

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = isRtl(locale) ? 'rtl' : 'ltr';
    setApiLocale(locale);
  }, [locale]);

  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l);
    try {
      localStorage.setItem(STORAGE_KEY, l);
    } catch {
      /* yok say */
    }
  }, []);

  const value = useMemo<I18nValue>(() => {
    const t = createTranslator(locale);
    const tag = INTL_TAG[locale];
    return {
      locale,
      setLocale,
      t,
      formatDate: (iso, withTime = true) => {
        if (!iso) return '—';
        const d = new Date(iso);
        return new Intl.DateTimeFormat(tag, {
          dateStyle: 'medium',
          ...(withTime ? { timeStyle: 'short' } : {}),
          timeZone: 'Europe/Istanbul',
        }).format(d);
      },
      formatNumber: (n, fractionDigits = 0) =>
        new Intl.NumberFormat(tag, {
          minimumFractionDigits: fractionDigits,
          maximumFractionDigits: fractionDigits,
        }).format(n),
      formatRelative: (iso) => {
        if (!iso) return t('common.never');
        const diff = (new Date(iso).getTime() - Date.now()) / 1000;
        const rtf = new Intl.RelativeTimeFormat(tag, { numeric: 'auto' });
        const abs = Math.abs(diff);
        if (abs < 60) return rtf.format(Math.round(diff), 'second');
        if (abs < 3600) return rtf.format(Math.round(diff / 60), 'minute');
        if (abs < 86400) return rtf.format(Math.round(diff / 3600), 'hour');
        return rtf.format(Math.round(diff / 86400), 'day');
      },
    };
  }, [locale, setLocale]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useI18n(): I18nValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('I18nProvider eksik');
  return v;
}

export function useT() {
  return useI18n().t;
}
